/**
 * EditorModelElement — a GLB 3D-model element in the editor, built on
 * EditorContentElement (web-core's CreatorElement base).
 *
 * The anchor is the transform/gizmo/pick handle; an instantiated copy of the GLB
 * (via web-core's shared loadModelInstance — the same path the boilerplates use)
 * is reparented under it at the model's authored real-world size. Selection uses
 * the panel's yellow box, sized to the model's STATIC bounds (measured once at
 * load, before animation) so it never breathes while animating.
 */

import {
  MeshBuilder,
  StandardMaterial,
  Color3,
  Vector3,
  Quaternion,
  type Mesh,
} from 'babylonjs';
import { loadModelInstance, unloadModelInstance, type IAssetModelInstance } from '@webcore/core/loader/loader.modelinstance';
import EditorContentElement from './EditorContentElement';

export class EditorModelElement extends EditorContentElement {
  private instanceAsset?: IAssetModelInstance;
  private placeholder: Mesh | null = null;

  protected scaleVector(scale: number[]): Vector3 {
    // Models keep a true 3D scale (the model is shown at its authored size).
    return new Vector3(scale[0], scale[1], scale[2]);
  }

  async build(): Promise<void> {
    const asset = await this.assetManager.getAsset(this.assetId);
    if (this._disposed) return;
    if (!asset) {
      console.warn(`Editor: skipping model ${this.sceneID} — asset not available`);
      this.dispose();
      return;
    }

    if (asset.container) {
      this.instanceAsset = { type: 'model', path: asset.assetPath, extension: '.glb' };
      await loadModelInstance(this.instanceAsset, this.scene, this.anchor);
      if (this._disposed) {
        unloadModelInstance(this.instanceAsset);
        return;
      }

      // Stamp metadata on every submesh so a click on any part resolves this element.
      const metadata = { editorElement: this, sceneID: this.sceneID };
      for (const m of this.anchor.getChildMeshes(false)) m.metadata = metadata;

      this.measureAndBuildOverlay();

      // Auto-play the instance's own animation groups (clones, not the template's).
      for (const group of this.instanceAsset.animationGroups ?? []) group.play(true);
    } else {
      // GLB failed to load → visible, selectable placeholder box.
      this.buildPlaceholder();
    }

    this.tweenScaleTo(this.targetScale);
  }

  /**
   * Measure the model's static bounds with the anchor at identity (world==local)
   * and build the selection box sized/centred to those native bounds. Restores
   * the anchor transform afterwards.
   */
  private measureAndBuildOverlay(): void {
    const savedPos = this.anchor.position.clone();
    const savedRot = this.anchor.rotationQuaternion ? this.anchor.rotationQuaternion.clone() : null;
    const savedScale = this.anchor.scaling.clone();

    this.anchor.position.setAll(0);
    this.anchor.rotationQuaternion = Quaternion.Identity();
    this.anchor.scaling.setAll(1);
    this.anchor.computeWorldMatrix(true);

    const { min, max } = this.anchor.getHierarchyBoundingVectors(true);
    const size = max.subtract(min);
    const center = min.add(max).scale(0.5);
    this.buildOverlay(size, center);

    this.anchor.position.copyFrom(savedPos);
    this.anchor.rotationQuaternion = savedRot;
    this.anchor.scaling.copyFrom(savedScale);
  }

  /** Solid translucent yellow selection box (same style as panels), sized to bounds. */
  private buildOverlay(size: Vector3, center: Vector3): void {
    this.overlay = MeshBuilder.CreateBox(
      `model_${this.sceneID}_overlay`,
      { width: Math.max(size.x, 0.001), height: Math.max(size.y, 0.001), depth: Math.max(size.z, 0.001) },
      this.scene,
    );
    this.overlay.parent = this.anchor;
    this.overlay.position = center;
    this.overlay.isPickable = false;
    this.overlayMat = new StandardMaterial(`model_${this.sceneID}_overlay_mat`, this.scene);
    this.overlayMat.disableLighting = true;
    this.overlayMat.emissiveColor = new Color3(1, 1, 0);
    this.overlayMat.backFaceCulling = false;
    this.overlayMat.alpha = 0;
    this.overlayMat.disableDepthWrite = true;
    this.overlay.material = this.overlayMat;
  }

  private buildPlaceholder(): void {
    this.placeholder = MeshBuilder.CreateBox(`model_${this.sceneID}_placeholder`, { size: 1 }, this.scene);
    this.placeholder.parent = this.anchor;
    this.placeholder.metadata = { editorElement: this, sceneID: this.sceneID };
    const mat = new StandardMaterial(`model_${this.sceneID}_placeholder_mat`, this.scene);
    mat.disableLighting = true;
    mat.emissiveColor = new Color3(0.67, 0.13, 0.13);
    this.placeholder.material = mat;
    this.buildOverlay(new Vector3(1, 1, 1), Vector3.Zero());
  }

  /** Models stay fully opaque (alpha-blending 3D geometry causes sort artifacts). */
  setOpacity(_opacity: number): void {
    /* no-op — the yellow selection box is the focus cue instead */
  }

  override handleHoverIn(): void {
    this.isHovered = true;
    this.setHighlight(this.isSelected ? 0.4 : 0.2);
  }

  override handleHoverOut(): void {
    this.isHovered = false;
    this.setHighlight(this.isSelected ? 0.4 : 0);
  }

  protected disposeVisual(): void {
    // Disposes this instance's nodes + animation groups — NOT the shared
    // template's materials (reused by sibling instances; the template is disposed
    // by the cache via AssetManager.cleanup → disposeModelCache).
    if (this.instanceAsset) unloadModelInstance(this.instanceAsset);
    this.placeholder?.material?.dispose();
    this.placeholder = null;
  }
}

export default EditorModelElement;
